<template>
  <ReviewsProductInsightsMarket
    :product="product"
    :ranking="view.ranking"
    :heroes="view.heroes"
    :charts="view.charts"
    :new-entrants="view.newEntrants"
    :hidden-new-entrants="view.hiddenNewEntrants"
    :top-ten="topTen.tools"
    :top-ten-state="topTen.state"
    :states="view.states"
    :loading="loading"
    :products-loading="productsLoading"
    :top-ten-loading="topTenLoading"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { type ProductById, useInsightsCategoryTopTenQuery, useInsightsProductsBySlugsQuery, useMarketInsightsQuery } from '@/gql/reviews'
import { mapCategoryTopTen, mapMarketInsights, marketProductSlugs } from '@/gql/reviews/mappers/insights'

import { comparisonWindow, isoToday } from '../insightsWindow'
import { useRouterQueryInsightsCategory } from '../useRouterQueryInsightsCategory'
import { useRouterQueryInsightsTimeframe } from '../useRouterQueryInsightsTimeframe'
import ReviewsProductInsightsMarket from './ReviewsProductInsightsMarket.vue'

const props = defineProps<{ product: ProductById }>()

const { locale } = useI18n()
const { category } = useRouterQueryInsightsCategory(() => props.product)
const { timeframe, periodPreset } = useRouterQueryInsightsTimeframe()

const comparison = computed(() => comparisonWindow(timeframe.value, new Date()))

const { result, loading } = useMarketInsightsQuery(
  () => ({
    productSlug: props.product.slug,
    entityId: category.value,
    period: periodPreset.value,
    startDate: comparison.value.previousStart ?? comparison.value.start,
    endDate: isoToday(new Date()),
  }),
  { clientId: 'reviews', keepPreviousResult: true, fetchPolicy: 'cache-first' },
)

const {
  result: topTenResult,
  loading: topTenLoading,
  error: topTenError,
} = useInsightsCategoryTopTenQuery(() => ({ categorySlug: category.value }), {
  clientId: 'reviews',
  keepPreviousResult: true,
  fetchPolicy: 'cache-first',
})

const slugs = computed(() => marketProductSlugs(result.value))

const { result: productsResult, loading: productsLoading } = useInsightsProductsBySlugsQuery(
  () => ({ slugs: slugs.value }),
  () => ({ clientId: 'reviews', fetchPolicy: 'cache-first', enabled: slugs.value.length > 0 }),
)

const view = computed(() =>
  mapMarketInsights(result.value, {
    locale: locale.value,
    products: productsResult.value?.listProducts ?? [],
    window: comparison.value,
  }),
)

const topTen = computed(() => mapCategoryTopTen(topTenResult.value, { locale: locale.value, failed: Boolean(topTenError.value) }))
</script>
